import React, { useState } from "react";
import {
  Modal,
  Button,
  Text,
  VisuallyHidden,
} from "@telegram-apps/telegram-ui";
import { BoostTypeEnum, PackIdEnum } from "../../handlers/types";
import ApiService from "../../handlers/apiService";
import { openInvoice } from "@telegram-apps/sdk";
import { useI18n } from "../../utils/i18n/I18nProvider";
import "./BoostPurchaseDialog.css";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";

interface BoostPurchaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  boostType:
    | BoostTypeEnum.Explosion
    | BoostTypeEnum.Heal
    | BoostTypeEnum.Revive;
  onPurchaseSuccess?: () => void;
}

interface BoostConfig {
  iconPath: string;
  displaySize: { width: number; height: number };
}

const BOOST_CONFIGS: Record<
  BoostTypeEnum.Explosion | BoostTypeEnum.Heal | BoostTypeEnum.Revive,
  BoostConfig
> = {
  [BoostTypeEnum.Explosion]: {
    iconPath: "/assets/img/ui/explosion.webp",
    displaySize: { width: 133, height: 133 },
  },
  [BoostTypeEnum.Heal]: {
    iconPath: "/assets/img/ui/heal.webp",
    displaySize: { width: 111, height: 111 },
  },
  [BoostTypeEnum.Revive]: {
    iconPath: "/assets/img/ui/revive.webp",
    displaySize: { width: 111, height: 111 },
  },
};

// Hardcoded prices as requested
const PRICES = {
  single: 25,
  bulk: 125,
  bulkQuantity: 10,
};

// Mapping from boost types to pack IDs for bulk purchases
const BOOST_TO_PACK_MAPPING: Record<
  BoostTypeEnum.Explosion | BoostTypeEnum.Heal | BoostTypeEnum.Revive,
  PackIdEnum
> = {
  [BoostTypeEnum.Explosion]: PackIdEnum.EXPLOSION10,
  [BoostTypeEnum.Heal]: PackIdEnum.HEAL10,
  [BoostTypeEnum.Revive]: PackIdEnum.REVIVE10,
};

export function BoostPurchaseDialog({
  isOpen,
  onClose,
  boostType,
  onPurchaseSuccess,
}: BoostPurchaseDialogProps) {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = BOOST_CONFIGS[boostType];

  // Get localized title and description
  const getBoostKey = (type: BoostTypeEnum) => {
    switch (type) {
      case BoostTypeEnum.Explosion:
        return "explosion";
      case BoostTypeEnum.Heal:
        return "heal";
      case BoostTypeEnum.Revive:
        return "revive";
      default:
        return "heal";
    }
  };

  const boostKey = getBoostKey(boostType);
  const title = t(`toolkit.shop.boostPurchaseDialog.${boostKey}.title`);
  const description = t(
    `toolkit.shop.boostPurchaseDialog.${boostKey}.description`
  );

  // Clear error after 5 seconds
  React.useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handlePurchase = async (quantity: 1 | 10) => {
    try {
      setIsLoading(true);
      setError(null);

      let invoiceUrl: string;

      if (quantity === 1) {
        // Single purchase: use boosts API
        const response = await ApiService.getInstance().api.boostsBuyCreate({
          boostType,
        });
        invoiceUrl = response.invoiceUrl;
      } else {
        // Bulk purchase: use packs API
        const packId = BOOST_TO_PACK_MAPPING[boostType];
        const response = await ApiService.getInstance().api.purchasesBuyCreate({
          packId,
        });
        invoiceUrl = response.invoiceUrl;
      }

      if (invoiceUrl && openInvoice.isAvailable()) {
        const status = await openInvoice(invoiceUrl, "url");

        if (status === "paid") {
          // Purchase successful
          onPurchaseSuccess?.();
          onClose();
        } else if (status === "failed") {
          setError(t("toolkit.shop.boostPurchaseDialog.paymentFailed"));
        }
        // For cancelled payments, just keep the dialog open
      } else {
        setError(t("toolkit.shop.boostPurchaseDialog.paymentNotAvailable"));
      }
    } catch (error) {
      console.error("Failed to purchase boost:", error);
      setError(t("toolkit.shop.boostPurchaseDialog.purchaseFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
        className="boost-dialog-modal"
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        aria-labelledby="boost-dialog-title"
      >
        <VisuallyHidden>
          <DialogDescription>{description}</DialogDescription>
          <DialogTitle>{title}</DialogTitle>
        </VisuallyHidden>

        <div
          className="boost-dialog-wrapper"
          role="dialog"
          aria-modal="true"
          aria-labelledby="boost-dialog-title"
        >
          {/* Title */}
          <Text
            id="boost-dialog-title"
            className={`boost-dialog-title ${boostKey}`}
          >
            {title}
          </Text>

          {/* Icon */}
          <div
            className="boost-dialog-icon"
            style={{
              backgroundImage: `url(${config.iconPath})`,
              width: `${config.displaySize.width}px`,
              height: `${config.displaySize.height}px`,
            }}
          />

          {/* Description */}
          <Text className="boost-dialog-description">{description}</Text>

          {/* Single purchase button */}
          <Button
            onClick={() => handlePurchase(1)}
            size="l"
            stretched
            className="boost-dialog-button"
            disabled={isLoading}
          >
            <span className="boost-dialog-button-text">
              {t("toolkit.shop.boostPurchaseDialog.singlePurchase", {
                price: PRICES.single,
              })}
            </span>
            <div className="boost-dialog-star-icon" />
          </Button>

          {/* Bulk purchase button */}
          <Button
            onClick={() => handlePurchase(10)}
            size="l"
            stretched
            className="boost-dialog-button"
            disabled={isLoading}
          >
            <span className="boost-dialog-button-text">
              {t("toolkit.shop.boostPurchaseDialog.bulkPurchase", {
                quantity: PRICES.bulkQuantity,
                price: PRICES.bulk,
              })}
            </span>
            <div className="boost-dialog-star-icon" />
          </Button>
        </div>
      </Modal>
  );
}

export default BoostPurchaseDialog;
